import React, { useState } from 'react';
import './FuncArea.css';
import * as XLSX from 'xlsx';
import ActionBar from '../Components/BottomActionBar/ActionBar';
import UpdateComponent from '../Components/UpdateComponent/UpdateComponent';
import { funcAreaAPI, batchStatusAPI } from './api';
import { invalidateFuncAreas } from './cache';
import DataTable from './DataTable';

function FuncArea() {
  const [funcAreaCode, setFuncAreaCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditCols, setShowEditCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});

  const [insertMsg, setInsertMsg] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [sortBy, setSortBy] = useState('');

  const handleInsert = async () => {
    setUpdateMsg(null);
    setDeleteMsg(null);
    
    if (!funcAreaCode.trim() || !name.trim() || !activeStatus) {
      alert('Functional Area Code, Name and Active Status are required fields.');
      return;
    }
    
    try {
      const res = await funcAreaAPI.create({
        func_sub_area_code: funcAreaCode,
        func_sub_area_name: name,
        ...(description.trim() !== '' && { func_area_desc: description }),
        active_status: activeStatus === 'Inactive' ? '0' : '1',
      });
      if (res.ok) { invalidateFuncAreas(); setInsertMsg({ text: 'Record inserted successfully.', success: true }); }
      else setInsertMsg({ text: 'Insert failed.', success: false });
    } catch {
      setInsertMsg({ text: 'Insert failed.', success: false });
    }
  };

  const handleUpdate = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(true);
    setEditRows({});
    const params = {};
    if (funcAreaCode.trim()) params.func_sub_area_code = funcAreaCode.trim();
    if (activeStatus === 'Active') params.active_status = '1';
    else if (activeStatus === 'Inactive') params.active_status = '0';
    const data = await funcAreaAPI.getAll(params);
    setTableData(data);
    setShowTable(true);
  };

  const toggleEditRow = (idx, row) => {
    setEditRows(prev => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: { func_sub_area_name: row.func_sub_area_name, func_area_desc: row.func_area_desc, active_status: (row.active_status === '1' || row.active_status === 'Active') ? 'Active' : 'Inactive' } };
    });
    
    setSelectedForUpdate(prev => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete(prev => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    const selectedIndexes = Object.keys(selectedForUpdate).filter(idx => selectedForUpdate[idx]);
    
    if (selectedIndexes.length === 0) {
      setUpdateMsg({ text: 'No records selected for update.', success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      const editedData = editRows[idx];
      
      if (!editedData) continue;

      try {
        const res = await funcAreaAPI.update(row.func_sub_area_code, {
          func_sub_area_code: row.func_sub_area_code,
          func_sub_area_name: editedData.func_sub_area_name,
          ...(editedData.func_area_desc?.trim() !== '' && { func_area_desc: editedData.func_area_desc }),
          active_status: editedData.active_status === 'Active' ? '1' : '0',
        });
        
        if (res.ok) {
          successCount++;
        } else {
          await res.json();
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      invalidateFuncAreas();
      setUpdateMsg({ text: `${successCount} record(s) updated successfully.`, success: true });
      const params = {};
      if (funcAreaCode.trim()) params.func_sub_area_code = funcAreaCode.trim();
      if (activeStatus === 'Active') params.active_status = '1';
      else if (activeStatus === 'Inactive') params.active_status = '0';
      const data = await funcAreaAPI.getAll(params);
      setTableData(data);
    }
    if (failCount > 0) {
      setUpdateMsg({ text: `${failCount} record(s) failed to update.`, success: false });
    }

    setSelectedForUpdate({});
    setEditRows({});
  };

  const handleDeleteSelected = async () => {
    const selectedIndexes = Object.keys(selectedForDelete).filter(idx => selectedForDelete[idx]);
    
    if (selectedIndexes.length === 0) {
      setDeleteMsg({ text: 'No records selected for delete.', success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];

      try {
        const inUseRes = await batchStatusAPI.checkInUse({ func_sub_area_code: row.func_sub_area_code });
        if (inUseRes.in_use) {
          alert('The Functional Area is currently in use.');
          continue;
        }
        const res = await funcAreaAPI.delete(row.func_sub_area_code);
        
        if (res.ok) {
          successCount++;
        } else {
          await res.json();
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      invalidateFuncAreas();
      setDeleteMsg({ text: `${successCount} record(s) deleted successfully.`, success: true });
      const params = {};
      if (funcAreaCode.trim()) params.func_sub_area_code = funcAreaCode.trim();
      if (activeStatus === 'Active') params.active_status = '1';
      else if (activeStatus === 'Inactive') params.active_status = '0';
      const data = await funcAreaAPI.getAll(params);
      setTableData(data);
    }
    if (failCount > 0) {
      setDeleteMsg({ text: `${failCount} record(s) failed to delete.`, success: false });
    }

    setSelectedForDelete({});
  };

  const handleView = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(false);
    setEditRows({});
    const params = {};
    if (funcAreaCode.trim()) params.func_sub_area_code = funcAreaCode.trim();
    if (activeStatus === 'Active') params.active_status = '1';
    else if (activeStatus === 'Inactive') params.active_status = '0';
    const data = await funcAreaAPI.getAll(params);
    setTableData(data);
    setShowTable(true);
  };

  const clearMessages = () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const handleExport = () => {
    const headers = ['Functional Area Code', 'Functional Area Name', 'Description', 'Active Status'];
    const data = tableData.map((row) => [
      row.func_sub_area_code,
      row.func_sub_area_name,
      row.func_area_desc,
      row.active_status === '1' ? 'Active' : 'Inactive',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Functional Area');
    XLSX.writeFile(wb, 'FuncArea.xlsx');
  };

  const handleReset = () => {
    setFuncAreaCode('');
    setName('');
    setDescription('');
    setActiveStatus('');
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setTableData([]);
    setShowTable(false);
    setSortBy('');
  };

  const sortTableData = (data, sortOption) => {
    if (!sortOption) return data;
    const sortedData = [...data];
    switch(sortOption) {
      case 'Functional Area Code':
        return sortedData.sort((a, b) => String(a.func_sub_area_code || '').localeCompare(String(b.func_sub_area_code || '')));
      case 'Functional Area Name':
        return sortedData.sort((a, b) => String(a.func_sub_area_name || '').localeCompare(String(b.func_sub_area_name || '')));
      case 'Active Status':
        return sortedData.sort((a, b) => (b.active_status === '1' ? 1 : 0) - (a.active_status === '1' ? 1 : 0));
      default:
        return sortedData;
    }
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (tableData.length > 0) {
      const sorted = sortTableData(tableData, sortOption);
      setTableData(sorted);
    }
  };

  return (
    <div className='Body' onClick={clearMessages}>
      <form>
        <div className='fa-form-wrap'>
          <table className='fa-form-table'>
            <tbody>
              <tr>
                <td className='fa-label'>Functional Area Code<span className='important'>*</span></td>
                <td><input className='codeInput' type='text' value={funcAreaCode} onChange={(e) => setFuncAreaCode(e.target.value)} onFocus={clearMessages} /></td>
              </tr>
              <tr>
                <td className='fa-label'>Name<span className='important'>*</span></td>
                <td><input className='nameInput' type='text' value={name} onChange={(e) => setName(e.target.value)} onFocus={clearMessages} /></td>
              </tr>
              <tr>
                <td className='fa-label'>Additional Description</td>
                <td><input className='descInput' type='text' value={description} onChange={(e) => setDescription(e.target.value)} onFocus={clearMessages} /></td>
              </tr>
              <tr>
                <td className='fa-label'>Active Status <span className='important'>*</span></td>
                <td>
                  <label><input type="radio" name="activeStatus" value="Active" checked={activeStatus === 'Active'} onChange={() => setActiveStatus('Active')} />{' '}Active</label>
                  &nbsp;&nbsp;&nbsp;
                  <label><input type="radio" name="activeStatus" value="Inactive" checked={activeStatus === 'Inactive'} onChange={() => setActiveStatus('Inactive')} />{' '}Inactive</label>
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <p className='remarks'><span className='important'>*</span> indicates a required field for 'INSERT' &amp; 'UPDATE'.</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </form>

      <ActionBar sortOptions={['Functional Area Code', 'Functional Area Name', 'Active Status']} onView={handleView} onReset={handleReset} onInsert={handleInsert} onUpdate={handleUpdate} onSortChange={handleSortChange} selectedSort={sortBy} />
      {insertMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: '"GEG-Body"', color: insertMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {insertMsg.text}
        </p>
      )}
      {updateMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: '"GEG-Body"', color: updateMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {updateMsg.text}
        </p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: '"GEG-Body"', color: deleteMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {deleteMsg.text}
        </p>
      )}

      {showTable && (
        <DataTable
          columns={[
            { header: 'Functional Area Code', key: 'func_sub_area_code', accessor: (row) => row.func_sub_area_code },
            { header: 'Functional Area Name', key: 'func_sub_area_name', accessor: (row) => row.func_sub_area_name, editable: true },
            { header: 'Description', key: 'func_area_desc', accessor: (row) => row.func_area_desc, editable: true },
            {
              header: 'Active Status',
              key: 'active_status',
              accessor: (row) => (row.active_status === '1' || row.active_status === 'Active') ? 'Active' : 'Inactive',
              editable: true,
              renderEdit: (idx, editRows, onChangeEditCell) => (
                <input
                  type="checkbox"
                  checked={editRows[idx]?.active_status === 'Active'}
                  onChange={(e) => onChangeEditCell(idx, 'active_status', e.target.checked ? 'Active' : 'Inactive')}
                />
              ),
            },
          ]}
          data={tableData}
          showEditCols={showEditCols}
          editRows={editRows}
          onToggleEditRow={toggleEditRow}
          onChangeEditCell={(idx, key, value) =>
            setEditRows(prev => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))
          }
          selectedForDelete={selectedForDelete}
          onToggleDeleteRow={toggleDeleteRow}
          emptyMessage="No data found"
          rowKey={(row, idx) => row.func_sub_area_code || idx}
        />
      )}
      {showTable && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button
            style={{ backgroundColor: '#810055', color: '#fff', border: 'none', padding: '5px 22px', borderRadius: '20px', cursor: 'pointer', fontFamily: '"GEG-Body"', fontSize: '12.5px' }}
            onClick={handleExport}
          >
            Export To Excel
          </button>
        </div>
      )}
      {showTable && showEditCols && (
        <UpdateComponent
          onUpdateSelected={handleUpdateSelected}
          onDeleteSelected={handleDeleteSelected}
        />
      )}
    </div>
  );
}

export default FuncArea;
